import { useState } from "react";
import { getApiUrl } from "../../utils/api";
import useFetchWithAuth from "./useFetchWithAuth";
import { updateUserDetailsCache } from "../../utils/authHelpers"; // Import the new function
import devLogger from "../../utils/devLogger";

export const useVerifyEmail = (
  initialEmail: string = ""
): UseVerifyEmailReturn => {
  const [currentStep, setCurrentStep] = useState<Step>("info");
  const [email, setEmail] = useState(initialEmail);
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { fetchWithAuth } = useFetchWithAuth();

  const handleSendCode = async () => {
    setError("");
    setIsLoading(true);

    try {
      // Send verification email using fetchWithAuth
      await fetchWithAuth(getApiUrl("/auth/verification-email"), {
        method: "POST",
      });

      setCurrentStep("token"); // Move to the token step after sending the code
    } catch (err: any) {
      setError(
        err.message || "Failed to send verification token. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendToken = async () => {
    setError("");
    setIsLoading(true);

    try {
      // Resend verification email using fetchWithAuth
      await fetchWithAuth(getApiUrl("/auth/verification-email"), {
        method: "POST",
      });

      // Show success message to user
      setError(""); // Clear any existing errors
    } catch (err: any) {
      setError(
        err.message || "Failed to resend verification token. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleTokenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (token.length !== 6 || !/^\d+$/.test(token)) {
      setError("Please enter a valid 6-digit token");
      return;
    }
    setError("");
    setIsLoading(true);

    try {
      // Verify email token using fetchWithAuth
      const res = await fetchWithAuth(getApiUrl("/auth/verify-email"), {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      devLogger.log("🚀 ~ res ~ res:", res);

      // Update the isEmailVerified flag in localStorage
      updateUserDetailsCache({ isEmailVerified: true });

      setCurrentStep("success");
    } catch (err: any) {
      setError(err.message || "Invalid verification token. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    currentStep,
    email,
    token,
    error,
    isLoading,
    setEmail,
    setToken,
    handleSendCode,
    handleResendToken,
    handleTokenSubmit,
  };
};

type Step = "info" | "token" | "success";

interface UseVerifyEmailReturn {
  currentStep: Step;
  email: string;
  token: string;
  error: string;
  isLoading: boolean;
  setEmail: (email: string) => void;
  setToken: (token: string) => void;
  handleSendCode: () => Promise<void>;
  handleResendToken: () => Promise<void>;
  handleTokenSubmit: (e: React.FormEvent) => Promise<void>;
}
